/**
 * Navigation helper to open authorisation details with resolved letter metadata for a J-code.
 * @module navigateToAuthorisationDetails
 */
import { NavigateFunction } from "react-router-dom";
import { AuthOnFile } from "../../../../../data-model/medicalPaOrdersModel";
import { getAllDocsForSingleJCode } from "../../../../../utils/getAllDocsForSingleJCode";
import { getAuthLetterDocUploadStatus } from "../../../../../utils/getDocsFromStatus";

/**
 * Arguments for {@link navigateToAuthorisationDetails}.
 */
interface NavigateToAuthorisationDetailsParams {
  jCodeId: string;
  jCode: string;
  authStatus: string;
  authOnFileDetails: AuthOnFile | null | undefined;
  orderId: string;
  navigate: NavigateFunction;
  letterDocPathOverride?: string;
  letterTitleOverride?: string;
  agentRerunLoading?: boolean;
}

/**
 * Navigates to the authorisation details page for a given J-code.
 *
 * Resolves the auth letter document path and title from the order's uploaded
 * documents, locates the full J-code details from auth entries, and navigates
 * to `/worklists/authorisation-details/:id` with the resolved state.
 *
 * @param params.jCodeId - Identifier of the J-code record.
 * @param params.jCode - The J-code value (e.g. "J9999").
 * @param params.authStatus - Current authorization status of the J-code.
 * @param params.singleMedicalPaOrder - The current medical PA order (may be null/undefined).
 * @param params.orderId - Fallback order ID used when the order object has no `id`.
 * @param params.navigate - React Router navigate function.
 */
export const navigateToAuthorisationDetails = ({
  jCodeId,
  jCode,
  authStatus,
  authOnFileDetails,
  orderId,
  navigate,
  letterDocPathOverride,
  letterTitleOverride,
  agentRerunLoading,
}: NavigateToAuthorisationDetailsParams): void => {
  const order = authOnFileDetails ?? {};
  const allDocs = getAllDocsForSingleJCode(jCodeId, order);
  const uploadedLetter = getAuthLetterDocUploadStatus(
    authStatus,
    allDocs ?? [],
    jCodeId,
    jCode,
  )?.[0];
  const jCodeDetails = (
    authOnFileDetails?.entries ?? authOnFileDetails?.auth_entries
  )
    ?.flatMap((e) => e.j_codes ?? [])
    .find((j) => j.id === jCodeId);
  const authLetterPath =
    letterDocPathOverride ??
    uploadedLetter?.doc_path ??
    jCodeDetails?.auth_letter?.auth_letter_path;
  navigate(`/worklists/authorisation-details/${orderId ?? ""}`, {
    state: {
      jCodeId,
      letterDocPath: authLetterPath ?? undefined,
      agentRerunLoading,
      letterTitle:
        letterTitleOverride ??
        uploadedLetter?.uploaded_text ??
        uploadedLetter?.doc_title ??
        "Letter",
      jCode: jCodeDetails ?? undefined,
    },
  });
};
